import { useState } from 'react'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { pageUrl } from '../seo'

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
}

// Uma letra por grupo, na ordem de glossario.groups no catálogo. As letras são
// as iniciais dos termos em português (fonte: OIM) e ficam iguais em todas as
// línguas — são um índice, não texto.
const LETTERS = ['A', 'B', 'E', 'I', 'M', 'N', 'R', 'T']

export default function Glossario() {
    const { t } = useTranslation()
    const [activeTab, setActiveTab] = useState(0)
    const groups = t('glossario.groups', { returnObjects: true })

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen bg-white"
        >
            {/* SEO por rota — no React 19 estas tags são içadas para o <head> */}
            <title>{t('glossario.metaTitle')}</title>
            <meta name="description" content={t('glossario.metaDescription')} />
            <link rel="canonical" href={pageUrl('glossario')} />
            {/* Hero Section */}
            <section
                className="relative overflow-hidden py-16 lg:py-20 bg-cover bg-center"
                style={{ backgroundImage: "url('/assets/images/hero-bg.jpg')" }}
            >
                <div className="absolute inset-0 bg-black/55" />
                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                    >
                        <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                            {t('glossario.title')}
                        </h1>
                    </motion.div>
                </div>
            </section>

            {/* Tabbed Glossary */}
            <section className="section-padding bg-slate-50">
                <div className="container-custom">
                    {/* Tab Navigation */}
                    <div className="flex flex-wrap gap-2 mb-8" role="tablist" aria-label={t('glossario.tabsAria')}>
                        {LETTERS.map((letter, i) => (
                            <button
                                key={letter}
                                role="tab"
                                id={`glossario-tab-${letter}`}
                                aria-selected={activeTab === i}
                                aria-controls={`glossario-painel-${letter}`}
                                aria-label={t('glossario.tabAria', { letter })}
                                tabIndex={activeTab === i ? 0 : -1}
                                translate="no"
                                onClick={() => setActiveTab(i)}
                                onKeyDown={(e) => {
                                    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
                                    e.preventDefault()
                                    const next = e.key === 'ArrowRight'
                                        ? (i + 1) % LETTERS.length
                                        : (i - 1 + LETTERS.length) % LETTERS.length
                                    setActiveTab(next)
                                    document.getElementById(`glossario-tab-${LETTERS[next]}`)?.focus()
                                }}
                                className={`w-12 h-12 rounded-xl font-bold text-lg transition-all ${
                                    activeTab === i
                                        ? 'bg-brand-600 text-white shadow-lg shadow-brand-200'
                                        : 'bg-white text-slate-700 border border-slate-200 hover:border-brand-300 hover:text-brand-600'
                                }`}
                            >
                                {letter}
                            </button>
                        ))}
                    </div>

                    {/* Tab Content.
                        Sem <AnimatePresence mode="wait">: com ele o painel
                        ficava preso na primeira letra — o filho a sair nunca
                        completava a saída, o novo nunca montava, e 12 das 13
                        entradas do glossário eram inacessíveis (o mesmo
                        acontecia em produção). Um motion.div com `key` faz o
                        mesmo fade sem depender da coreografia de saída. */}
                    <div>
                        <motion.div
                            key={activeTab}
                            role="tabpanel"
                            id={`glossario-painel-${LETTERS[activeTab]}`}
                            aria-labelledby={`glossario-tab-${LETTERS[activeTab]}`}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.25 }}
                            className="space-y-6"
                        >
                            {(groups[activeTab] ?? []).map((entry, i) => (
                                <motion.div
                                    key={i}
                                    variants={fadeUp}
                                    initial="hidden"
                                    animate="visible"
                                    transition={{ duration: 0.4, delay: i * 0.07 }}
                                    className="bg-white rounded-2xl p-7 shadow-xs border border-slate-100"
                                >
                                    <h3 className="text-base font-bold text-brand-700 uppercase tracking-wide mb-3">
                                        {entry.term}
                                    </h3>
                                    <p className="text-slate-700 leading-relaxed">{entry.definition}</p>
                                    {entry.bullets && entry.bullets.length > 0 && (
                                        <ul className="mt-3 space-y-2">
                                            {entry.bullets.map((b, j) => (
                                                <li key={j} className="text-slate-700 flex items-start gap-2 leading-relaxed">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0 mt-2" />
                                                    {b}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>

                    {/* Source */}
                    <motion.p
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="mt-10 text-sm text-slate-500 italic"
                    >
                        {t('glossario.source')}
                    </motion.p>
                </div>
            </section>
        </motion.div>
    )
}
